/* eslint-disable max-lines-per-function */
import { useState, useRef, useEffect } from "react";
import { cn } from "tailwind-variants";
import UseSvg from "common/vue/components/UseSvg.react.vue";
import { useDtfStore } from "./store";
import "./LetterNumbersStep.scss";

export default function LetterNumbersStep({
  label,
  step,
  initiallyOpen = false,
  children,
  slots,
  ...restProps
}) {
  const store = useDtfStore();
  const [isOpened, setIsOpened] = useState(initiallyOpen);

  // Computed properties as functions
  function isLetters() {
    return store.isLetters;
  }

  function letterNumberArtwork() {
    return store.letterNumberArtwork;
  }

  function letterHeightAndMaxLength() {
    const artwork = store.letterNumberArtwork;
    const { height, maxLength } = artwork || {};
    return Boolean(height && maxLength);
  }

  function loadFromConfig() {
    return store.loadFromConfig;
  }

  function handleToggle() {
    setIsOpened(!isOpened);
  }

  // Get current values for watch dependencies
  const artwork = letterNumberArtwork();
  const style = artwork?.style;
  const font = artwork?.font;
  const height = artwork?.height;
  const color = artwork?.color;
  const maxLength = artwork?.maxLength;
  const heightAndMaxLength = letterHeightAndMaxLength();

  // Watch letterNumberArtwork.style for step 2
  const prevStyleRef = useRef(undefined);
  useEffect(() => {
    const oldValue = prevStyleRef.current;

    if ([2].includes(step)) {
      if (!oldValue && style) {
        setIsOpened(true);
      }
      if (oldValue && style === null && !initiallyOpen) {
        setIsOpened(false);
      }
    }

    prevStyleRef.current = style;
  }, [style]);

  // Watch letterNumberArtwork.font for step 3 (letters only)
  const prevFontRef = useRef(undefined);
  useEffect(() => {
    if (isLetters()) {
      const oldValue = prevFontRef.current;

      if ([3].includes(step)) {
        if (!oldValue && font) {
          setIsOpened(true);
        }
        if (oldValue && font === null && !initiallyOpen) {
          setIsOpened(false);
        }
      }

      prevFontRef.current = font;
    }
  }, [font, isLetters()]);

  // Watch letterHeightAndMaxLength for step 4 (letters only)
  const prevHeightMaxLengthRef = useRef(undefined);
  useEffect(() => {
    if (isLetters()) {
      const oldValue = prevHeightMaxLengthRef.current;

      if ([4].includes(step)) {
        if (!oldValue && heightAndMaxLength) {
          setIsOpened(true);
        }
      }

      prevHeightMaxLengthRef.current = heightAndMaxLength;
    }
  }, [heightAndMaxLength, isLetters()]);

  // Watch letterNumberArtwork (deep) for step 4 (letters only) - check if all null
  useEffect(() => {
    if (isLetters()) {
      if ([4].includes(step)) {
        if (artwork?.height === null && artwork?.maxLength === null && artwork?.color === null) {
          setIsOpened(false);
        }
      }
    }
  }, [JSON.stringify(artwork)]);

  // Watch letterNumberArtwork.color for step 5 (letters only)
  const prevColorRef = useRef(undefined);
  useEffect(() => {
    if (isLetters()) {
      const oldValue = prevColorRef.current;

      if ([5].includes(step)) {
        if (!oldValue && color) {
          setIsOpened(true);
        }
        if (oldValue && color === null && !initiallyOpen) {
          setIsOpened(false);
        }
      }

      prevColorRef.current = color;
    }
  }, [color, isLetters()]);

  // Watch letterNumberArtwork.height for step 3 (numbers only)
  const prevHeightRef = useRef(undefined);
  useEffect(() => {
    if (!isLetters()) {
      const oldValue = prevHeightRef.current;

      if ([3].includes(step)) {
        if (!oldValue && height) {
          setIsOpened(true);
        }
        if (oldValue && height === null && !initiallyOpen) {
          setIsOpened(false);
        }
      }

      prevHeightRef.current = height;
    }
  }, [height, isLetters()]);

  // Watch letterNumberArtwork.color for steps 4, 5 (numbers only)
  const prevNumbersColorRef = useRef(undefined);
  useEffect(() => {
    if (!isLetters()) {
      const oldValue = prevNumbersColorRef.current;

      if ([4, 5].includes(step)) {
        if (!oldValue && color) {
          setIsOpened(true);
        }
        if (oldValue && color === null && !initiallyOpen) {
          setIsOpened(false);
        }
      }

      prevNumbersColorRef.current = color;
    }
  }, [color, isLetters()]);

  // onMounted
  useEffect(() => {
    if (!loadFromConfig()) {
      setIsOpened(true);
    }
  }, []);

  return (
    <div {...restProps} className={cn("letter-numbers-step", restProps.className)}>
      <div
        className="letter-numbers-step__title"
        onClick={handleToggle}
        data-testid="letter-numbers-step-title"
      >
        <div className="letter-numbers-step__label">
          {" "}
          Step {step}: <span className="letter-numbers-step__name">{label}</span>
          {isOpened ? slots?.action?.() : null}
        </div>
        <UseSvg
          className={cn(
            "letter-numbers-step__icon",
            isOpened && "letter-numbers-step__icon--opened"
          )}
          icon="chevron-down"
          data-testid="use-svg"
        />
      </div>
      <div
        className="letter-numbers-step__content"
        style={{
          display: isOpened ? undefined : "none"
        }}
      >
        {children}
      </div>
    </div>
  );
}
